import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Chip,
  Grid,
  Typography,
  IconButton,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@material-ui/icons/Close';
import useStyles from './styles';
import { AuthContext } from '../../contexts';
import { COUNTRY_ID } from '../../localization/features/constants';

const NewsModal = (props) => {
  const classes = useStyles();
  const { onClose } = props;
  const authContext = useContext(AuthContext);
  const { country } = authContext.state;
  const { t } = useTranslation();

  const renderTag = (category) => {
    let label = '';
    let type = '';
    switch (category) {
      case 'annoucements':
        type = 'annoucement';
        label = 'Annoucements';
        break;
      case 'new_features':
        type = 'new';
        label = 'New features';
        break;
      case 'improvements':
        type = 'improvement';
        label = 'Improvements';
        break;
      default:
        break;
    }
    return (
      <Chip
        className={`${classes.chip} ${type}`}
        variant="outlined"
        label={label}
      />
    );
  };

  // Add new contents here
  const contents = [
    {
      version: '1.151.0',
      release: 'Aug 05, 2026',
      updates: [
        {
          category: 'improvements',
          notes: [
            'You can now use the same Listing ID for both iProperty and PropertyGuru channels, following the merger of the two portals. Duplicate Listing IDs within the same portal are still prevented, so lead routing stays accurate.',
            'Fixed an issue where disabling the “Email assignee when a lead or opportunity is assigned” workflow did not stick — the automation was recreated automatically and assignees kept receiving emails. Disabled workflows now stay disabled.',
            'Improved Avanser call tracking number handling in Call settings: numbers now activate reliably once Avanser submits their form, and numbers with missing or invalid tracking details correctly remain Pending instead of being marked Active.',
            'Renamed the sidebar menu item under Settings › Campaign from “Campaign settings” to “Call settings” so it matches the page it opens.',
            'Fixed an intermittent error that prevented new entries from being created in Workflow automation.',
            'Various minor performance enhancements and bug fixes for smoother overall experience.',
          ],
        },
      ],
    },
    {
      version: '1.150.0',
      release: 'Jul 08, 2026',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Added Excel export functionality for Opportunities, allowing users to download opportunity data for monthly tracking and sales performance reporting.',
            'Fixed phone number validation in invalid submissions to correctly accept numbers with country codes, allowing users to update and save lead data without errors.',
            'Upgraded the Meta Marketing API integration to v25.0, ensuring uninterrupted Facebook lead capture and synchronization while preventing issues from deprecated API versions.',
            'Fixes custom Action Statuses were not displayed correctly on the Lead Listing page. Previously, the system defaulted to a standard status instead of showing the selected custom value. The Lead stage column now accurately reflects the chosen custom status, improving pipeline visibility and tracking accuracy.',
          ],
        },
      ],
    },
    {
      version: '1.149.0',
      release: 'Jan 22, 2026',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Introduced customizable Lead Action Statuses feature, allowing HQ users to create and manage statuses by stage category, with updates reflected immediately across lead management screens.',
            'Introduced a Salesperson (Assignee) menu that provides a clear overview of active assignees and their lead workload, helping managers monitor distribution and follow-up performance.',
            'Improved customer import handling to correctly support records without NRIC, ensuring accurate data storage and consistency.',
            'Fixed an issue where unsubscribed recipients were still receiving email blasts. Unsubscribed users will now stop receiving all future email communications as expected.',
            'Various minor performance enhancements and bug fixes for smoother overall experience.',
          ],
        },
      ],
    },
    {
      version: '1.148.0',
      release: 'Dec 18, 2025',
      updates: [
        {
          category: 'improvements',
          notes: [
            {
              text: 'Localized the CRM for Indonesia: lead status “New” now displays as “Baru” for easier use.',
              onlyShowCountry: [COUNTRY_ID],
            },
            'Optimized the Customer Details page to reduce loading time for customers with multiple opportunities, providing a faster and smoother experience.',
            'Updated the WhatsApp link on the Leads Information page to the official API, allowing reliable and seamless chat initiation from the CRM.',
            'Improved the customer import process to prevent stalling and provide timely progress updates for smoother uploads.',
            'Fixed an issue where some opportunity details were not displaying correctly, ensuring all financial information is accurate and complete.',
            'Fixed an issue where numeric fields (e.g., “Number of Pax”) in form submissions were not included in exported reports, ensuring accurate data in all exports.',
            'Fixed an issue where unsubscribed recipients were still receiving email blasts. Unsubscribed users will now stop receiving all future email communications as expected.',
            'Fixed an issue where customer imports without NRIC could result in incorrect or missing data. Customer records are now imported accurately even when NRIC is not provided.',
            'Various minor performance enhancements and bug fixes for smoother overall experience.',
          ],
        },
      ],
    },
    {
      version: '1.147.0',
      release: 'Oct 6, 2025',
      updates: [
        {
          category: 'improvements',
          notes: [
            {
              text: 'Introduced KTP-based search in MHub Indonesia, allowing you to find leads and customers quickly using their KTP number.',
              onlyShowCountry: [COUNTRY_ID],
            },
            {
              text: 'Adjust price delimiters (Rp), improving data entry consistency and readability for Indonesian standart.',
              onlyShowCountry: [COUNTRY_ID],
            },
            'Resolved an issue that was preventing the SalesCandy integration from working correctly.',
            'Fixed a bug where text within the EDM feature was not being saved accurately.',
            'Corrected an issue to ensure that customer information updates are now saved accurately and reliably.',
            'Addressed a bug where the lead filter was not always returning accurate results.',
            'Various minor performance enhancements and bug fixes for smoother overall experience.',
          ],
        },
      ],
    },
    {
      version: '1.146.0',
      release: 'Aug 6, 2025',
      updates: [
        {
          category: 'improvements',
          notes: [
            'SalesCandy webhook syncing now supports multi-tenant setups, with up to 5 simultaneous connections, making integrations more scalable and flexible.',
            'Exporting Leads and Customers is now 30% faster than ever.',
            'Reserve button in Opportunity now works correctly, with the navigation leading to Siteplan as expected',
            'Various minor bug fixes for enhanced stability and performance.',
          ],
        },
      ],
    },
    {
      version: '1.145.0',
      release: 'July 7, 2025',
      updates: [
        {
          category: 'improvements',
          notes: [
            {
              text: 'Our platform now supports Bahasa Indonesia (beta). Some areas may still appear in English as we continue to enhance localization.',
              onlyShowCountry: [COUNTRY_ID],
            },
            'Lead reassignment now supports last activity within the past 1 hour.',
            'You can now export the Monthly Income field without any issues.',
            'Notes on customer and lead pages are now paginated correctly.',
            'Various minor bug fixes for enhanced stability and performance.',
          ],
        },
      ],
    },
    {
      version: '1.144.0',
      release: 'June 19, 2025',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Submissions linked to a single project ID now display the correct project name.',
            'Audience Filtering using Exclude function has now been improve.',
            'Various minor bug fixes for enhanced stability and performance.',
          ],
        },
      ],
    },
    {
      version: '1.143.0',
      release: 'April 2, 2025',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Facebook Insight and Ads submission from the same ID can now exist under the same campaign.',
            'Improved Email Blast: Users can now set a budget before publishing to ensure smooth delivery.',
            'Audience groups are now displayed correctly, even without selecting any projects.',
            'Various minor bug fixes for enhanced stability and performance.',
          ],
        },
      ],
    },
    {
      version: '1.142.0',
      release: 'Mar 4, 2025',
      updates: [
        {
          category: 'improvements',
          notes: [
            'The calendar now correctly display appointments on the last day of each month.',
            'Email/SMS blast credit balance is now visible directly under the campaign page, based on the campaign business unit.',
            'Various minor bug fixes for better stability and performance.',
          ],
        },
      ],
    },
    {
      version: '1.141.0',
      release: 'Feb 10, 2025',
      updates: [
        {
          category: 'improvements',
          notes: [
            'New Blank Template for Email Blast, allowing you to build your email messages from scratch with greater flexibility.',
            'Enhanced saving mechanism for a smoother experience when doing Email and SMS blast.',
            'Stability & Performance Enhancements – Various bug fixes for a more reliable system.',
          ],
        },
      ],
    },
    {
      version: '1.140.0',
      release: 'Jan 15, 2025',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Credit Check is now enabled for leads assigned from different Business Units.',
            'Updated Facebook Marketing API to latest version.',
            'Various minor bug fixes for enhanced stability and performance.',
          ],
        },
      ],
    },
    {
      version: '1.139.0',
      release: 'Nov 27, 2024',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Email Blast: Users can now manually save their designs with a "Save Design" button for added ease of mind.',
            'Email Blast: A confirmation popup will now appear when switching between tabs to prevent accidental unsaved changes.',
            'Various minor bug fixes for enhanced stability and performance.',
          ],
        },
      ],
    },
    {
      version: '1.138.0',
      release: 'Nov 13, 2024',
      updates: [
        {
          category: 'improvements',
          notes: [
            'You can now manually toggle audience email subscriptions between subscribed and unsubscribed.',
            'Users will receive email notifications when a lead/opportunity is merged via the automerge workflow.',
            'Updated mapping for SalesCandy users: Lead Source and Original Source are now attached to leads from SalesCandy.',
            'Various minor bug fixes for enhanced stability and performance.',
          ],
        },
      ],
    },
    {
      version: '1.137.0',
      release: 'Nov 4, 2024',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Find Facebook Forms and Ads Insights faster with our updated search box.',
            'Campaign metrics are now showing Cost per Lead and Cost per Qualified Lead to help track ad performance.',
            'Minor bug fixes to improve stability and performance.',
          ],
        },
      ],
    },
    {
      version: '1.136.0',
      release: 'Oct 7, 2024',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Improved audience group logic, now allows the creation of Audience based on Opportunity Stage.',
            'Export Filtered View is now working again, enabling users to export leads according to their filter.',
            'Minor bug fixes and UI improvements.',
          ],
        },
      ],
    },
    {
      version: '1.135.0',
      release: 'Sept 23, 2024',
      updates: [
        {
          category: 'improvements',
          notes: [
            'TIN validated on Showroom will no longer be editable in CRM.',
            'Addressed issue with FB Authorisation timeout when using Personal Login.',
            'User may now apply No project and No source filter for both All Leads and All Customers page.',
            'SalesCandy Data Mapping now support multiple project mapping in CRM.',
            'SMS content now updated to adhere with MCMC\'s latest policy regarding Bulk SMS',
            'Minor bug fixes and UI improvements.',
          ],
        },
      ],
    },
    {
      version: '1.134.0',
      release: 'Sept 5, 2024',
      updates: [
        {
          category: 'improvements',
          notes: [
            'TIN fields from Showroom are now synced to CRM.',
            'Improved SalesCandy fields syncing.',
            'Minor bug fixes and security improvements.',
          ],
        },
      ],
    },
    {
      version: '1.133.0',
      release: 'August 20, 2024',
      updates: [
        {
          category: 'annoucements',
          notes: [
            'Meta Business Suite users can now authorise their Facebook Pages and Ads via Facebook Login for Business, eliminating the 60-day token renewal. This provides permanent access unless revoked by Meta',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'You can now choose to authorise Facebook from either your personal account, or your Meta Business Suite account.',
            'Customer audience list are now displayed properly when user selects specific date range.',
            'Minor bug fixes and security improvements.',
          ],
        },
      ],
    },
    {
      version: '1.132.0',
      release: 'July 24, 2024',
      updates: [
        {
          category: 'improvements',
          notes: [
            'New TIN field under Personal Information tab for Customer.',
            'Improved phone number handling for Malaysian numbers.',
            'Minor bug fixes.',
          ],
        },
      ],
    },
    {
      version: '1.131.0',
      release: 'July 11, 2024',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Resync button will be displayed in Facebook settings permanently for user\'s convenience to sync their adset details',
            'Minor bug fixes.',
          ],
        },
      ],
    },
    {
      version: '1.130.0',
      release: 'June 25, 2024',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Enhanced Facebook querying to reduce timeout issues and enable seamless connection of pages and forms.',
            'Minor bug fixes.',
          ],
        },
      ],
    },
    {
      version: '1.129.0',
      release: 'June 11, 2024',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Streamlined the auto-merge lead to lead function to enhance usability.',
            'Enhanced Avanser integration, user can now choose integration type either inbound only, or inbound and outbound.',
            'Minor bug fixes.',
          ],
        },
      ],
    },
    {
      version: '1.128.0',
      release: 'May 27, 2024',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Added resync icon to allow users to synchronise their adset access.',
            'Bookings done through CRM now default to \'CRM\' as a source.',
            'Minor bug fixes.',
          ],
        },
      ],
    },
    {
      version: '1.127.0',
      release: 'May 13, 2024',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Improved Visibility for FB Adset Access: Users with limited FB Adset access can now view affected submissions under All Submissions.',
            'Minor bug fixes.',
          ],
        },
      ],
    },
    {
      version: '1.126.0',
      release: 'Apr 30, 2024',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Added on dates now reflect properly, based on the lead/customer\'s initial creation date.',
            'L2 users should now be able to export customer\'s data correctly.',
            'Minor bug fixes.',
          ],
        },
      ],
    },
    {
      version: '1.125.0',
      release: 'Apr 18, 2024',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Users are now able to export all leads, including those deleted, merged and unqualified.',
            'Minor bug and performance fixes.',
          ],
        },
      ],
    },
    {
      version: '1.124.0',
      release: 'Apr 2, 2024',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Facebook adset details are now included in lead export.',
            'Level II (b) - Agency Admin, Agent Leader can now export leads assigned under their Business Unit.',
            'Pagination now working properly when viewing the bounced email list.',
            'Fixed campaign channel’s alert warning showing even though no error was found.',
            'Minor bug and UI fixes.',
          ],
        },
      ],
    },
    {
      version: '1.123.0',
      release: 'Mar 18, 2024',
      updates: [
        {
          category: 'improvements',
          notes: [
            'You can now export QR codes for MHub form in higher resolution, either in .svg or .png formats',
            'For existing customers, opportunities created from Customer Form will merge directly into their profile.',
            'Fixed issue with exiting/cancelling out when viewing the Read Instruction popup.',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.122.1',
      release: 'Mar 11, 2024',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Inbound leads from Avanser are now permanently stored in submissions for improved referencing.',
            'We\'ve removed the Zendesk widget. For assistance, reach out directly to our careline at +6019-6756118 or via email at support@mhub.my',
          ],
        },
      ],
    },
    {
      version: '1.122.0',
      release: 'Mar 06, 2024',
      updates: [
        {
          category: 'improvements',
          notes: [
            'The auto-merge workflow now include opportunities when merging leads into exisiting customers.',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.121.0',
      release: 'Feb 19, 2024',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Level III Staff/Agents can now export their assigned leads and customers.',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.120.1',
      release: 'Feb 08, 2024',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Project names are now displayed correctly when exporting selected or filtered customer data.',
            'Customer export function can now handle exporting over 10,000 rows.',
          ],
        },
      ],
    },
    {
      version: '1.120.0',
      release: 'Feb 05, 2024',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Email blast feature will now support more than 1,000 audience list.',
            'Improved Avanser call tracking and answer point activation method.',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.119.1',
      release: 'Feb 02, 2024',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Email bounced lists are now displayed correctly.',
            'Sending test and email blast will now display from name and email text preview properly.',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.119.0',
      release: 'Jan 17, 2024',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Booking Conversion rate in Campaign Performance now includes Won and Booked Opportunities for better accuracy.',
            'To avoid confusion with file imports, every Customer import files is now required to have unique file names.',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.118.0',
      release: 'Jan 03, 2024',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Customer Name now displays accurately in the Opportunity Log.',
            'Form submissions with duplicate NRICs are crosschecked with existing customers, and matching ones will be merged.',
            'MHub Customer Form submissions with duplicate NRICs are crosschecked and matching ones will be merged with with existing customers.',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.117.0',
      release: 'Dec 19, 2023',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Now you can also auto-merge duplicate leads as a new opportunity to the existing customers  based on the condition settings through Auto-merge Workflow',
            'Fixed the top search bar not being able to search for customers by user role level 2 and level 3',
            'Fixed opportunities incorrectly appearing in irrelevant customer profiles',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.116.0',
      release: 'Dec 05, 2023',
      updates: [
        {
          category: 'new_features',
          notes: [
            '[New Workflow] Seamlessly auto-merge duplicate leads into previous or incoming leads based on the condition settings through Automation Workflow',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'The Reserve button in Opportunity will now be disabled for projects that do not have any remaining available units',
            'Fixed Opportunity-related Activity log in customer profile to display as customer\'s name instead of gibberish customer ID',
            'Fixed campaign performance metrics for qualified, unqualified, and duplicate submissions to show count accurately',
            'Fixed lead avatar with spaced names to display properly',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.115.0',
      release: 'Nov 22, 2023',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Appointment metrics are now accessible for L2 and L3 users, and they can filter their appointments by clicking on the metrics above the appointment calendar',
            'You may now add appointment directly within the appointment tab on the Customer\'s profile',
            'Notes updated upon appointment completion are now visible on the Activity Log and Note of the Lead or Customer\'s profile',
            'The Notes column will now include the assignee\'s name for easier referencing when exporting customers',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.114.0',
      release: 'Nov 08, 2023',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Any changes on the appointments including creation, rescheduling and status update will be shown on Activity log of the customer and other appointment attendee\'s profile',
            'You may now filter with 0 Opportunity by ticking "Show only customers without opportunity" in the Customer listing filter',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.113.0',
      release: 'Oct 25, 2023',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Now you can view and manage all your appointments with our new Appointment calendar! Remember to update your appointment status. Appointment activity log on the customer profile will be coming up in the next release',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'UTM fields will be included by default when exporting leads',
            'Fixed filter for assignees and filtered count on the lead listing',
            'Fixed the count of booking when customer was imported with an invalid opportunity stage. You can click on the “detected invalid stage” link to update affected opportunity with the correct stage',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.112.0',
      release: 'Oct 11, 2023',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Fixed user role level 2 unable to view the customer profile which consists of an opportunity that has been assigned to them',
            'Fixed no record in the exported customer file when selecting specific customer data to be exported',
            'Fixed form_url validation after clicking Save that bypasses the validity of the URL entered',
            'Fixed download button for exported files that are expired but are not appearing as disabled buttons',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.111.0',
      release: 'Sep 13, 2023',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Facebook API is now upgraded to v17.0, please re-authorise your Meta Business Account under Facebook settings',
            'Now you will be alerted when creating an Appointment that overlaps with the assignee’s existing appointment’s date and time',
            'Filtering submissions by date range will export the submissions that fall within the specified date range',
            'When linking a Listing Portal channel, the project dropdown will display only the projects associated with the campaign',
            'Fixed exported file access showing expired when downloading Lead / Customer',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.110.0',
      release: 'Aug 24, 2023',
      updates: [
        {
          category: 'new_features',
          notes: [
            'PropertyGuru channel is now integrated with PropertyGuru\'s webhook. Contact our MHub team for more info',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Fixed error when importing Customers with empty Opportunity Project ID and empty Opportunity Stage columns',
            'Show email and MyKad for reference when merging duplicated leads and customers',
            'You can now filter leads / customers / opportunities by deleted projects',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.109.0',
      release: 'Aug 02, 2023',
      updates: [
        {
          category: 'improvements',
          notes: [
            'You can now search your customers just by using their NRIC number',
            'Lead and customer profile will now inform users on customers with the same NRIC',
            'Rescheduling appointments can only be done at a maximum of 5 times per appointment',
            'Added a counter in Appointment Information to show the number of times the appointment has been rescheduled',
            'Changes for appointment note in Notes will also be reflected in Appointment Information',
            'Labeling for Form ID and Form Name is now displayed correctly in Submission Information',
            'Duplicated leads with existing customer will convert and merge into existing customer when Qualified in Leads',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.108.0',
      release: 'Jul 20, 2023',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Now you can auto-fill bookings in Showroom with your Customer\'s passport',
            '"Add link" button is removed for SMS blast in accordance to Malaysian Communications And Multimedia Commission\'s (MCMC) directive',
            'Added auto-mapping when linking pre-filled fields of a Facebook form',
            'Added customisable appointment type field and appointment status',
            'Assignee of the Opportunity is now required in order to create an appointment',
            'Appointment status will automatically set from Upcoming to Pending when the appointment is past due',
            'Changing Appointment status to Completed will prompt user to leave a note for future reference',
            'Changing Appointment status to Cancelled will prompt user to key in Cancellation Reason',
            'Users are not allowed to reassign Opportunities with Upcoming/Pending appointments, unless the appointment status is changed to Cancelled/Completed',
            'Fixed the assignee field in Customer listing page showing n/a instead of "Unassigned" when there\'s unassigned opportunities. Opportunity assignee will show as n/a when there\'s no opportunity created under that customer.',
            'Fixed Customer\'s activity log showing "null" when updating customer\'s information in Showroom',
            'Fixed form url validation for website integration',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.107.0',
      release: 'Jun 01, 2023',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Fixed user role access control for agent leader and agency admin, now these roles will have read and write access to the leads or customers assigned to the agents of the particular business units',
            'Fixed source field shows up during lead qualification even though editing the Source of leads from lead generation is not permitted',
            'Now Submissions API accepts a boolean value',
            'Changes on a note will also be reflected on Last updated on and Last updated by fields',
            'Show time in Last updated on field',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.106.0',
      release: 'May 11, 2023',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Now during lead qualification of an existing customer, the sales staff can validate and modify the customer\'s information if there are discrepancies between the new information and the current data (for required fields only)',
            'Added validation into the email parser of PropertyGuru channel to verify the email sender to prevent lead generated from phishing emails',
            'Added last update activity log to Facebook settings, user role level 1 can also view the expiration date of your Facebook access token',
            'Show in lead/customer activity log when a note is deleted',
            'Fixed lead data failing to transfer to the existing customer profile after qualification',
            'Fixed exported lead file to exclude deleted custom fields columns and empty columns',
            'Fixed Facebook page selection list to only show linked Facebook page when creating new Facebook form channel',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.105.0',
      release: 'Apr 05, 2023',
      updates: [
        {
          category: 'new_features',
          notes: [
            '[New page] Added "Potential opportunities" page for you to review deals that have not yet closed at your convenience',
            '[Notification] User role level 1 will receive an email notification when your Facebook access token is about to expire in 10 days and needs to be re-authorised',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'MHub forms now support toggling MyKad or Passport field according to Nationality field. Add these 3 fields into your MHub forms to trigger this behavior',
            'Added back button on lead/customer profile, campaign and audience listing',
            'Fixed copy and frequency on Facebook re-authorisation modal to pop-up once everyday when it expires in 7 days',
            'Fixed the sorting of notes on lead/customer profile',
            'Fixed activity log that showing no value when adding or editing emergency contact information',
            'Fixed exporting filtered view instead of exporting all leads or submissions',
            'Fixed standard field (eg. Allow buyer access) not displaying in field setting and appear as compulsary when qualifying leads',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.104.0',
      release: 'Mar 20, 2023',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Now sales staff will be prompted to fill out the required fields for creating a customer profile during lead qualification, ensuring that all necessary information is obtained',
            'Fixed the export limitation for user role level 2, which now allows them to only export records of their own business unit.',
            'Fixed unintentional auto reassignment on manually created leads',
            'Fixed mobile number validation on MHub CRM forms',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.103.0',
      release: 'Mar 01, 2023',
      updates: [
        {
          category: 'improvements',
          notes: [
            'User role level 1 can now create campaign and import leads for other business units',
            'Fixed access for user role level 2 to view or search leads/customers/opportunities of the same business unit',
            'Now exporting leads/customers/submissions, we will notify you via email when the file is ready for you to download when you have time. This is to prevent exporting large file being terminated due to time out',
            'Improved UI on the Facebook re-authorisation reminder pop-up to create urgency',
            'Added Unqualified reason as a default column onto Unqualifed leads listing page',
            'Fixed custom fields not showing correct field label when exported to CSV file',
            'Fixed preferred salesperson/agent field submitted via MHub Customer form to map correct assignee if provided valid user\'s email address',
            'Fixed opportunity_assigned_to column via bulk import customer to map correct assignee if provided valid user\'s email address',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.102.0',
      release: 'Feb 14, 2023',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Now you can import one or more opportunities with its assignee by bulk importing customers',
            'Enabled editing on invalid submissions to validify them as leads',
            'Fixed missing "Already taken" text in taken opportunities',
            'Fixed validation on disabled required fields when editing lead or customer',
            'Fixed broken links on inactive campaign channel tooltips',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.101.0',
      release: 'Jan 31, 2023',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Added Sold opportunity stage into campaign sales funnel chart for a complete campaign performance visualization',
            'Added filter and column for lead qualification date in customer listings',
            'Added normalisation when importing lead source with space and special characters',
            'Fixed username displaying in MHub account credit log',
            'Fixed missing details in auto assignment when users previously added were now non-existent/deleted',
            'Fixed column alignment in lead and customer data export file',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.100.0',
      release: 'Jan 10, 2023',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Now you can edit your budget and actual expenses on listing portal channels to ensure the accuracy of your cost per lead',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.99.0',
      release: 'Dec 28, 2022',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Now you can map the Date of Birth field format (eg. MM/DD/YYYY) for Facebook and website integration, and it will be converted to our format (YYYY-MM-DD) automatically',
            'Trace UTM data for each opportunity of a customer after qualifying lead',
            'All level III roles are no longer able to unassign themselves from a lead or an opportunity',
            'Project names selected by lead/customer via MHub forms are now shown in the submission information',
            'Added indication when country code is missing on a mobile field',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.98.0',
      release: 'Dec 14, 2022',
      updates: [
        {
          category: 'new_features',
          notes: [
            '[New page] Check out the list of invalid submissions and their error messages under Campaigns if lead profile was not created upon submission after integration configurations were completed',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Improved UI design of auto-assignment settings\' activity log for you to skim through more easily',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.97.0',
      release: 'Dec 01, 2022',
      updates: [
        {
          category: 'new_features',
          notes: [
            '[New fields] Capture UTM data when you share MHub form with UTM parameters in the URL, or provide form_url with UTM parameters via our Submissions API for your website integration',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Enhanced and updated the rule structure of auto-assignment to support your needs. Now you can set multiple campaigns or projects to be auto-assigned to the same user group',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.96.1',
      release: 'Nov 22, 2022',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Enhanced call settings and call workflow to utilise activated individual dialer number for better call tracking',
          ],
        },
      ],
    },
    {
      version: '1.96.0',
      release: 'Nov 17, 2022',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Now you can better visualise your campaign performance with sales funnel chart, by clicking on the funnel icon in campaign performance section',
            'Gender and Date of Birth can now be auto-generated when your customers fill up MyKad (NRIC) field in your MHub form, or, when you merge a lead with MyKad (NRIC)',
            'Fixed issue where lead profile unable to be created when an invalid date format (Date of Birth) found in the submission received',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.95.0',
      release: 'Nov 02, 2022',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Added "Select all" option to selected multi-selections filters (eg. Source, Opportunity stage, Lost reason)',
            'Added columns for merged leads in Deleted leads listing',
            'Gender can now be derived from MyKad (NRIC) field',
            'Facebook form linkage is now restricted to only one campaign to prevent lead duplication',
            'Fixed Project name, Campaign name and SRB project access\' rule conditions to use OR operator instead of AND in Audience group configuration',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.94.0',
      release: 'Oct 20, 2022',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Now you can determine a default source for your MHub forms if it would only be used in one location (eg. Walk-in or Event)',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Now you can look for Unqualified leads using the search bar',
            'And you can also merge leads if there is duplicate lead found in the unqualified bucket',
            'The State field on an MHub Form will lists down Malaysia states if user has selected Malaysia for the Country field',
            'Users can only enter numbers when filling up MyKad(NRIC) field for standardisation',
            'Fixed lead profile’s activity log for not showing updated data of number field type (eg. Postcode)',
            'Minor fixes',
          ],
        },
      ],
    },
    {
      version: '1.93.1',
      release: 'Oct 13, 2022',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Fixed issue of views on website channel disappearing after clicking on the channel to view details',
            'Fixed issue of prolonged reassignment time interval for leads that are captured after 8PM',
            'Fixed issue of skipping reassignment when the assignee had updated lead information before',
          ],
        },
      ],
    },
    {
      version: '1.93.0',
      release: 'Oct 06, 2022',
      updates: [
        {
          category: 'new_features',
          notes: [
            '[New page] Now you can directly view the list of Lost opportunities and its lost reason',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Added Cancellation reason field synchronized from booking in Showroom to opportunity in CRM',
            'Added Booking metric in Campaign performance to enable holistic view of sales funnel',
            'Added an Add Lead button on Homepage, start adding new lead with lesser clicks',
            'Added more accepted values for Bumiputera status (eg. "bumiputera", "non_bumiputera" and "not_applicable" in addition to "yes", "no" and "n/a") when you bulk import or submit with Submissions API integration',
            'Added Social media as an option for Source field',
            'Fixed duplicated metric to only check duplication within the Campaign\'s submissions',
            'Fixed placement of Title field (eg. Mr/Ms/Mrs) when added onto MHub form',
            'Names are capitalised in display mode (raw data will stay unchanged) to keep our user interface consistent and harmonious',
            'Minor fixes',
          ],
        },
      ],
    },
    {
      version: '1.92.0',
      release: 'Sep 22, 2022',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Now importing Customer CSV file with empty column will not remove existing customer data to prevent data loss',
            'Added PropertyGuru (integration channel) in Source field and filters',
            'Listing portal submissions are now part of Reach metric in campaign performance',
            'Display MHub form name, instead of auto assignment name, on lead activity log if it is assigned based on the preferred assignee selected in the MHub form',
            'Facebook ads and forms selection in Campaign channel show only relevant options of the connected Facebook page(s)',
            'Stopped deriving last updated date from lead to opportunity during lead qualification',
            'Display last updated customer name on Customer listing page',
            'Fixed missing last updated date and person data on Customer listing page',
            'Fixed accessibility issue for salesperson/agents to view the profile of customers that are assigned to them',
            'Fixed browser notification linkage issue',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.91.0',
      release: 'Sep 08, 2022',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Now you can redirect to external URL upon MHub form submission',
            'Campaign summary auto-refresh time has been adjusted to refresh at 12 midnight daily',
            'Automatically convert unformatted columns in CSV file to snake case when importing lead and customer',
            'Fixed missing multiselection values submitted via MHub Customer form',
            'Fixed pagination issue where listing does not return to first page after clicking on menu',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.90.0',
      release: 'Aug 29, 2022',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Now you can have an overview of your campaign performance. Track and evaluate your marketing campaign with lead and booking conversion rate',
          ],
        },
      ],
    },
    {
      version: '1.89.1',
      release: 'Aug 25, 2022',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Updated UI for channel filters on Campaign page',
            'Fixed credit deduction issue',
            'Fixed \'id required\' error when scheduling an email blast',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.89.0',
      release: 'Aug 11, 2022',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Added Activity log for any changes made on Auto assignment',
            'Added support for non-English character on MHub forms and custom fields',
            'Improved delayed reassignment of leads for rule with last activity of 30 minutes',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.88.0',
      release: 'July 28, 2022',
      updates: [
        {
          category: 'new_features',
          notes: [
            'New field type! Now you can create multiselections field type to capture multiple choices via MHub form',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Synchronize Opportunity assignee with assigned staff of the booking in Showroom',
            'Fixed missing submission information',
            'Fixed incorrect auto assignment name in activity log',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.87.1',
      release: 'July 18, 2022',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Generate leads from PropertyGuru in MHub CRM when you receive enquiry email notification. Contact our MHub team for more info',
          ],
        },
      ],
    },
    {
      version: '1.87.0',
      release: 'July 14, 2022',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Added Consideration stage field when qualifying lead to better quantify customer potential',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Redefined lost reasons for Opportunity and unqualified reasons for Unqualifed leads',
            'Know who and when the lead is deleted on Deleted leads listing',
            'User will be prompted if there is no rule set when creating new auto-assignment rule',
            'Email field on MHub forms now have email format validation implemented',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.86.0',
      release: 'Jun 30, 2022',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Sales staff or agents can download credit reports of the customers that are added by them if you allow them in Credit check settings',
            'Now you can glance at the information of new leads or opportunity assigned to you and engage them right away on your mobile',
            'Importing leads with assignee field but not in email address format will be Unassigned. Follow the sample CSV format to import leads without errors',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.85.0',
      release: 'Jun 16, 2022',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Get notified as soon as you are assigned to a new lead or opportunity. Allow notifications on your browser now! It works on mobile devices too!',
            'Directly call your leads/customers from your mobile devices and log it into their profiles',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Now you can integrate with multiple iProperty developer accounts and stream the leads to MHub CRM',
            'Added preset field mapping to website integration channel',
            'Minor bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.84.0',
      release: 'May 19, 2022',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Enhanced user experience on navigation by adding back button on Campaign channel, Workflow and Auto assignment configuration pages',
            'Added call history for your call tracking numbers',
            'Call tracking will only be able to be accessed by companies that have subscribed to the call package. Contact our MHub team for more info because more features are coming up!',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.83.0',
      release: 'Apr 28, 2022',
      updates: [
        {
          category: 'new_features',
          notes: [
            '[It\'s back] Chat with our Customer Success team on MHub CRM',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Now you can reorder the sequence of your lead auto-assignment',
            'Make sure call tracking name is unique to avoid double counting of inbound calls',
            'Fixed opportunity count to reflect accurately on customer listing',
            'Fixed exported file to include data for lead aging and submission source',
          ],
        },
      ],
    },
    {
      version: '1.82.0',
      release: 'Apr 20, 2022',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Assignees will not receive email notification on new lead assignment for bulk imported leads',
            'Minor bug fixed',
          ],
        },
      ],
    },
    {
      version: '1.81.0',
      release: 'Apr 14, 2022',
      updates: [
        {
          category: 'annoucements',
          notes: [
            '[Removed] After a round of evaluation, we have decided to remove Report feature from CRM until further notice. Stay tune!',
          ],
        },
        {
          category: 'new_features',
          notes: [
            'As promised, more feature on call tracking. You can now listen to call recording for inbound and outbound call made from CRM',
            'Call tracking will only be able to be accessed by companies that have subscribed to the call package. Contact our MHub team for more info because more features are coming up!',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'To better suit your needs, you can now define when not to reassign new leads, either after qualifying leads or reassign ONLY new leads',
          ],
        },
      ],
    },
    {
      version: '1.80.0',
      release: 'Apr 01, 2022',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Now you can login your Avanser account on MHub CRM to request call tracking numbers and setup answer points',
            'Automated inbound call lead creation from your printed media (e.g, billboard, bunting, flyer or brochure) with call tracking',
            'Call tracking will only be able to be accessed by companies that have subscribed to the call package. Contact our MHub team for more info because more features are coming up!',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Enhanced search algorithm on customer data, able to dig into customers\' previous names or emails if changed',
          ],
        },
      ],
    },
    {
      version: '1.79.0',
      release: 'Mar 25, 2022',
      updates: [
        {
          category: 'annoucements',
          notes: [
            '[Removed] After a round of evaluation, we have decided to remove Chat feature until further notice',
          ],
        },
        {
          category: 'new_features',
          notes: [
            'New channel! Register your print media advertising budget, and actual spent in a campaign, e.g, billboard, bunting, flyer or brochure',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Now you can filter submissions by Source, e.g, see all submissions from Facebook without going through campaign by campaign',
            'Stopped sending excessive new lead assignment email notification',
            'Allow type to search in the Country field',
            'Added option to open link in new tab on MHub forms',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.78.0',
      release: 'Mar 09, 2022',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Add lead or opportunity to a specific non-integrated campaign channel (e.g. SMS blast, email blast, event) and vice versa',
            'Or, add campaign channel name column to your Lead and Opportunity table listing if it suits',
            'Make your life easier by searching instead of scrolling through any long drop-down list',
            'Call workflow now supports call assignee then opportunity\'s primary customer from a campaign or a single project',
            'Call workflow will only be able to be accessed by companies that have subscribed to the call package. Contact our MHub team for more info because more features are coming up!',
          ],
        },
      ],
    },
    {
      version: '1.77.0',
      release: 'Feb 16, 2022',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Standardised statuses of forms and channels in all campaigns',
            'Now you can specify a form for your auto-assignment rule',
            'Able to select multiple forms within a condition when you\'re configuring a workflow',
            'When a purchaser is removed from an opportunity, they will be shown as "Removed customer" for that opportunity',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.76.0',
      release: 'Feb 10, 2022',
      updates: [
        {
          category: 'new_features',
          notes: [
            'As you already know, we have added notification badge on What\'s new, so that you are always up-to-date with our new features or improvements',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Now you can see which auto-assignment is assigning or re-assigning the particular lead on the activity log',
            'Improved form selection field loading speed in workflow, auto-assignment configuration and filters',
            'Invalid email counts are now displaying correctly',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.75.0',
      release: 'Jan 27, 2022',
      updates: [
        {
          category: 'annoucements',
          notes: [
            'MHub Lead is now known as MHub CRM!',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Setup workflow by choosing a form under a campaign',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.74.0',
      release: 'Jan 19, 2022',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Chat with our Customer Success team on MHub Lead',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'The floating contact button (Call, Whatsapp or Email) is now positioned on top of the lead/customer profile (Click on the 3 dots)',
            'You can now see exactly what time the lead/customer/opportunity is added',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.73.0',
      release: 'Jan 13, 2022',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Manual calls are now trackable or you can still manually log your calls if you prefer',
            'Filter your leads by caller, call time and date range or call outcome (eg. No answer, Line busy, etc)',
            'Table will refresh after assigning or deleting leads / customers / opportunities / submissions',
            'Date of birth can be derived from MyKad (NRIC) field',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.72.0',
      release: 'Jan 05, 2022',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Manual and automated call logs can be viewed easily with a UI update that shows the labels accordingly',
            'Making a booking for a customer via opportunity now requires NRIC before proceeding to the booking form in Showroom',
            'Customer profile, audience group and email blast will now show invalid emails. These emails are not reachable and will not be able to use the email blast feature',
            'Users can now view a suggested action in the lead profile that are related to call logs',
            'You can now view the assignee of each opportunity upfront on the customer profile page',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.71.0',
      release: 'Dec 23, 2021',
      updates: [
        {
          category: 'new_features',
          notes: [
            '[New workflow] Email reminder when lead/opportunity is inactive. This automation handles sending a reminder email to the assignee and emailing a CC (carbon copy) to other salespeople or users as long as they are an MHub user. Customise your email content and apply them to different campaigns or projects',
            '[New workflow] Call assignee then lead from submission. This automation enables automated calls to be triggered to the assignee and once contacted, the system will trigger another call to the lead upon submission of lead. Power up your team\'s productivity!',
            'Call workflow will only be able to be accessed by companies that have subscribed to the call package. Contact our MHub team for more info',
            'You can view the email reminders being sent in the activity log section of the respective leads or customers',
          ],
        },
      ],
    },
    {
      version: '1.70.0',
      release: 'Dec 16, 2021',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Call logs are now available for lead and customer. Log a manual call and select various call outcomes to help you keep track of the progress. ',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'You may now share specific page of the listing as our pagination now remembers which page you are at',
            'Audience groups for customers can now be grouped by SRB project access or ID',
          ],
        },
      ],
    },
    {
      version: '1.69.0',
      release: 'Dec 08, 2021',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Our workflow module is ready! With workflows, you may configure certain triggers like email or call via intelligent rule configuration. Automate your processes to ensure higher efficiency for your team! Read more in our support page',
            '[New workflow] Email assignee when lead/opportunity is assigned. This automation cannot be disabled and will apply for every assignment',
            '[New workflow] Email others on new lead/opportunity. This automation handles emailing a CC (carbon copy) to other salespeople or users as long as they are an MHub user. Customise your email content and apply them to different campaigns or projects',
          ],
        },
      ],
    },
    {
      version: '1.68.0',
      release: 'Dec 01, 2021',
      updates: [
        {
          category: 'improvements',
          notes: [
            'You can now filter leads by lead aging days since the lead was added',
            'Lead aging days will now appear in red when aged more than 7 days',
            'You will no longer be able to edit customers mobile numbers if the customer proflie has an active MHub Showroom for Buyer (SRB) account to ensure data accuracy',
          ],
        },
      ],
    },
    {
      version: '1.67.0',
      release: 'Nov 24, 2021',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Project and unit dropdown fields now able to return more than 1k results if have',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.66.0',
      release: 'Nov 17, 2021',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.65.0',
      release: 'Nov 10, 2021',
      updates: [
        {
          category: 'new_features',
          notes: [
            'You can now duplicate and reuse an existing email blast template from any campaign into another campaign without the need to set everything up from scratch again',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.64.0',
      release: 'Nov 03, 2021',
      updates: [
        {
          category: 'improvements',
          notes: [
            'You can now easily identify your role, company name, and company branch name under profile menu',
            'Lead’s ID can be now viewed inside lead listing and will also be included when export it',
            'Audience group can now be grouped with three new rules: Import ID, Last updated on, and Last updated by',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.63.0',
      release: 'Oct 27, 2021',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Now you can view lead aging inside lead listing and lead profile.',
            'Now you can view who and when a customer being imported',
            'Now you can view unit booking history of a customer inside their profile',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.62.0',
      release: 'Oct 21, 2021',
      updates: [
        {
          category: 'improvements',
          notes: [
            'You can now view more than 10k records in Customer listing, Opportunity Listing, Campaign Listing, and Submission Listing.',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.61.0',
      release: 'Oct 13, 2021',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Now you can select specific columns that you would like to export for leads.',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.60.0',
      release: 'Oct 05, 2021',
      updates: [
        {
          category: 'new_features',
          notes: [
            'New channel: Link your website external forms by requesting for an API key, add more information as budget, submissions, and actual spent.',
            'Integrate Iproperty forms and stream the leads to MHub, budget, submissions, actual spent, listing URL.',
            'Now you can store up to 10,000 leads',
            'Map the fields from your external form to MHub fields.',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Active/ Inactive Facebook integration status. Inactive (when lead stream is broken and needs to be re-linked)',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.59.0',
      release: 'Oct 05, 2021',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.58.0',
      release: 'Sep 08, 2021',
      updates: [
        {
          category: 'improvements',
          notes: [
            'The reasons for bounced emails are now visible at email overview page.',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.57.0',
      release: 'Aug 25, 2021',
      updates: [
        {
          category: 'new_features',
          notes: [
            'New type of form is now available, where you can create the customer instead of the lead (with IC or Passport field as compulsory to fill)',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.56.0',
      release: 'Aug 19, 2021',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Now you can create new opportunity if existing one is lost already.',
            'Update customers data in bulk by using Customer ID (import feature).',
            'View the credit check status of each customer.',
            'The email charges have been updated, for more info visit our support page.',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.55.0',
      release: 'Aug 11, 2021',
      updates: [
        {
          category: 'improvements',
          notes: [
            'You can now qualify a lead to a new customer without selecting any project. This is useful if your project is currently unavailable or has sold out. Salesperson or agent can add a note for future reference',
            'Customer activity log has improved and now keeps the historical data',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.54.0',
      release: 'Aug 04, 2021',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Credit check settings: Select user(s) to send email notification for new Credit Check by internal staff',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'You can now filter opportunities by lost reasons',
            'Group audiences by new rules like imported on, imported by, added on, date of birth, project name without campaign',
            'Filter customers by assignee and lost reason',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.53.0',
      release: 'Jul 28, 2021',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Run credit check on customer profile and get the DSR score, max loan eligibility and monthly commitments',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Leads from MHub form submissions are now being shared to business units according to the person that created the form',
            'Projects that users have no access to are clearly labelled',
            'You can now select more lost reasons like size too big, size too small and opted for another project',
            'Updating previous imports are now possible to correct values that you may want to change but already have imported them',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.52.0',
      release: 'Jul 21, 2021',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Mobile navigation improvements',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.51.0',
      release: 'Jul 15, 2021',
      updates: [
        {
          category: 'improvements',
          notes: [
            'All level II and III roles are no longer able to delete leads',
            'Deleted leads can now be viewed in a separate menu',
            'All level III roles are no longer able to export leads and customers',
            'Level III roles are no longer able to create campaign and view campaign or channel metrics',
            'You can now view assignee column in the customer listing',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.50.0',
      release: 'Jul 08, 2021',
      updates: [
        {
          category: 'improvements',
          notes: [
            'You can now filter customers by project name and campaign name',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.49.0',
      release: 'Jul 01, 2021',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Email blasts now supports URL or website link tracking. Insert your links and track them after the email is sent',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'New quick filters are now available and located on the listing pages',
          ],
        },
      ],
    },
    {
      version: '1.48.0',
      release: 'Jun 23, 2021',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Email notification content for assigned leads has been updated with project name',
            'Improved email verification for sender email to prevent emails to be marked as spam',
            'Fixed an issue with lead export date format',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.47.0',
      release: 'Jun 16, 2021',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Auto assignment now supports OR rule condition. Assign leads with more flexibility',
            'You may also auto assign leads from multiple sources to the same users',
            'UI improvements',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.46.0',
      release: 'Jun 11, 2021',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Auto assignment has been updated to a more comprehensive interface that includes user groups and reassignment in together',
            'UI improvements for filter and form T&C',
          ],
        },
      ],
    },
    {
      version: '1.45.0',
      release: 'Jun 2, 2021',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Email recipients can now unsubscribe from your marketing. The customer subscription will be updated at the audience group',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Email verification will now show statuses like pending code and verifying if the verification is not complete',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.44.0',
      release: 'May 25, 2021',
      updates: [
        {
          category: 'improvements',
          notes: [
            'View email blast confirmation section in the overview page after an email is sent',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.43.0',
      release: 'May 20, 2021',
      updates: [
        {
          category: 'improvements',
          notes: [
            'SMS cost will now reflect additional cost if the SMS exceeds limit due to personalised variables letters that are not included in the GSM character set',
            'SMS failure has been refined. Read more about what it includes at the support center',
            'Lead stage has been updated with 2 different contacted options',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.42.0',
      release: 'May 12, 2021',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Personalise your email blast further by selecting your own “from” sender email. Verify your domain first at the email verification settings',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'View email insights like scheduled, sent, opens and bounced',
            'Add the assignee field to your MHub form so leads that submit will be automatically assigned to that salesperson directly',
            'Add and map the assignee field to your Facebook form so leads that submit will be automatically assigned to that salesperson directly',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.41.0',
      release: 'May 05, 2021',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Fixed an issue with the email blast template alignment',
            'View imported on and imported by information for every lead and customer',
          ],
        },
      ],
    },
    {
      version: '1.40.0',
      release: 'Apr 28, 2021',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Send an email blast to grouped audiences to nurture potential customers and convert opportunities to sales',
            'Schedule the email in advance to be sent later with our email scheduler',
            'Design beautiful emails with our email builder! Add images, links and much more',
            'Email templates available to help you get started with quick creation',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Auto assign leads by project alone without campaign',
            'Campaign listing has been updated for easier viewing',
            'Search users by role or business unit when selecting an assignee or adding a user group',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.39.0',
      release: 'Apr 21, 2021',
      updates: [
        {
          category: 'improvements',
          notes: [
            'View chat participants when you start a chat or add someone new into the chat group',
          ],
        },
      ],
    },
    {
      version: '1.38.0',
      release: 'Apr 14, 2021',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Fixed a bug where facebook users are not able to unauthorise account',
            'You can now view the campaign name in the csv file when you export customers or leads',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.37.0',
      release: 'Apr 7, 2021',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Duplicate forms to speed up the form creation process',
            'Group audience by campaign or project',
            'Set the booking limit for customers that will control the booking limit on MHub Showroom for Buyer',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.36.0',
      release: 'Mar 31, 2021',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Import customers that are qualified by your sales team straight into MHub. Follow the sample csv format to get started',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.35.0',
      release: 'Mar 25, 2021',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Campaign overview and channel listing has a new look with added campaign metrics',
            'MHub chat is here! Chat with buyers that have registered on the MHub Showroom for Buyer web app',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.34.0',
      release: 'Mar 17, 2021',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Preview for SMS is now available',
            'View SMS insights like scheduled, sent, clicks and delivered after the SMS blast has been sent',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.33.0',
      release: 'Mar 15, 2021',
      updates: [
        {
          category: 'new_features',
          notes: [
            'MHub account is now available for view for all Headquarters role. Click on the profile dropdown at the top right hand corner to access. Check your company credit balance as well as spending history there',
            'Group customers via audience group for marketing purposes',
            'Send an SMS blast to grouped audiences to nurture potential customers and convert opportunities to sales',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.31.0',
      release: 'Feb 24, 2021',
      updates: [
        {
          category: 'improvements',
          notes: [
            'An in-app re-authorisation reminder will be shown when Facebook data authorisation has been interrupted due to security changes',
          ],
        },
      ],
    },
    {
      version: '1.30.0',
      release: 'Feb 17, 2021',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.29.0',
      release: 'Feb 11, 2021',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Facebook settings has been relocated to main settings',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.28.0',
      release: 'Feb 03, 2021',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Facebook API update',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.27.0',
      release: 'Jan 27, 2021',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Map fields from Facebook form to MHub fields',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'View all suspended users when assigning leads or opportunities',
            'View all suspended users when creating user groups for auto assignment',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.26.0',
      release: 'Jan 20, 2021',
      updates: [
        {
          category: 'improvements',
          notes: [
            'We\'ve added a 30 minutes rule option for auto re-assignment',
            'Auto assignment will exclude suspended users',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.25.0',
      release: 'Jan 13, 2021',
      updates: [
        {
          category: 'new_features',
          notes: [
            'View your form submission growth rate at the form overview drawer. The percentage compares this week\'s submissions versus last week\'s',
            'Send a message to our support team via contact us option from the top right help icon',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.24.0',
      release: 'Jan 06, 2021',
      updates: [
        {
          category: 'new_features',
          notes: [
            'MHub chat is here! Chat with buyers that have registered on the MHub Showroom for Buyer web app',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'We\'ve added some additional restrictions to lead merging. Non-headquarters roles can only merge leads that are assigned to you',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.23.0',
      release: 'Dec 23, 2020',
      updates: [
        {
          category: 'new_features',
          notes: [
            'MHub forms has a brand new look!',
            'Our new MHub form builder is here! Equipped with few handy templates for quick creation.',
            'Add new form questions from field settings',
            'Add custom form questions',
            'Share the form URL on social media',
            'Download the QR code for any form',
          ],
        },
      ],
    },
    {
      version: '1.22.0',
      release: 'Dec 16, 2020',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Customer activity log has been relocated to the side panel in the customer profile page for easier viewing',
            'Auto assignment now supports rule by race',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.21.0',
      release: 'Dec 14, 2020',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Exporting leads and customers will now include notes',
            'Activity log inconsistency has been corrected',
          ],
        },
      ],
    },
    {
      version: '1.20.0',
      release: 'Dec 03, 2020',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Auto assignment now supports assignments by project by campaigns',
            'Changes have been made to disallow changes to the primary customer in opportunities to ensure accuracy',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.19.0',
      release: 'Nov 25, 2020',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Customers can only have one unique opportunity to avoid duplicate opportunities',
            'Lead activity log will now include imported details',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.18.0',
      release: 'Nov 18, 2020',
      updates: [
        {
          category: 'improvements',
          notes: [
            'More fields have been added to the customer filter',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.17.0',
      release: 'Nov 11, 2020',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Lead activity log has been relocated to the side panel in the lead profile page for easier viewing',
            'Source field choices are now customisable. Head to the all fields menu in the settings page to add on your preferred sources',
            'View customer information in the customer profile and use quick edit to make fast changes to fields',
            'Exporting leads and customers now supports filtered views. Apply desired filter and export by selecting the option from the more button at listing pages.',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.16.0',
      release: 'Nov 05, 2020',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Activity log will now show fields deletion history',
            'Lead profile has undergone a slight facelift for better navigation',
          ],
        },
      ],
    },
    {
      version: '1.15.0',
      release: 'Oct 28, 2020',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Edit lead fields easily on desktop by clicking on each field at the lead profile',
            'Average response time for assignee in the sales performance report has been optimised for a more accurate reflection',
            'Activity log will now show first assignment',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.14.0',
      release: 'Oct 21, 2020',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Campaigns can now include more than one project and it will be reflected in all submissions and leads from that campaign',
            'UI improvements',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.13.0',
      release: 'Oct 14, 2020',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Select more than one project name that a lead is interested in',
            'Auto assignment rules are trimmed for better logic and experience',
            'UI improvements for exporting and bulk assigning',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.12.0',
      release: 'Oct 07, 2020',
      updates: [
        {
          category: 'new_features',
          notes: [
            'You can now merge duplicate leads of up to 3 at a time. Select the values you want to retain and merge to resolve duplicates',
            'We have combined customer and contact module for easier viewing and management',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.11.0',
      release: 'Sep 30, 2020',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Auto assignment will now assign the lead back to the same salesperson if the lead is already an existing record',
            'Sales reports improvements',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.10.0',
      release: 'Sep 23, 2020',
      updates: [
        {
          category: 'new_features',
          notes: [
            'When selecting assignee, you can now view their mobile number, business unit name and role for easier identification',
            'Sales performance report now includes an assignee\'s average response time',
            'View unqualified leads separately in the secondary navigation bar',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Sales reports improvements',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.9.0',
      release: 'Sep 15, 2020',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Save filters are now available for lead, customer and contact records',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Creating a customer with existing mobile or email is not allowed to prevent duplication ',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.8.0',
      release: 'Sep 2, 2020',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Qualified leads can be merged to existing customers to lessen potential duplicates',
            'When a lead is contacted, select outcome of the contact made to keep track of your interaction',
            'Auto reassignment of leads is now active from 8am to 8pm only',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.7.0',
      release: 'Aug 26, 2020',
      updates: [
        {
          category: 'new_features',
          notes: [
            'NEW report module! Click on the report icon on the main menu bar to view',
            'New report! Sales reports are now available for all HQ user roles',
            'New report! Sales performance reports are now available for all HQ user roles',
            'Filter contacts according to custom fields',
            'New \'Buyer type\' field includes options first-time buyer, repeat buyer and future buyer for better categorisation',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.6.0',
      release: 'Aug 21, 2020',
      updates: [
        {
          category: 'new_features',
          notes: [
            'New channel! Link facebook campaigns to monitor performance',
            'New channel! Link your microsite or landing page to gain insights',
            'New campaign listing features combined insights to help you compare performance',
          ],
        },
      ],
    },
    {
      version: '1.5.0',
      release: 'Aug 12, 2020',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Connect your facebook pages account to receive leads from facebook',
            'Filter customers by appointment date',
            'View submission response from lead or opportunity submission source',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.4.0',
      release: 'Aug 5, 2020',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Opportunity stages will now sync with Showroom app\'s booking status',
            'View new registration from Showroom for Buyer at the customer activity log',
            'Filter leads by project name and campaign name',
            'Filter opportunities by campaign name',
            'Filter contacts according to SRB registration date',
            'View bookings for opportunities with bookings from Showroom',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.3.0',
      release: 'Jul 24, 2020',
      updates: [
        {
          category: 'new_features',
          notes: [
            'New site plan/sales chart button located at the opportunity information drawer. Make bookings easily by referring to the site plan or sales chart at our Showroom app',
            'New reserve button located at the bottom of the opportunity information drawer. Make bookings faster by selecting a unit and clicking to reserve',
            'Include project name in your campaigns so submissions, leads and opportunities are automatically linked to specific project from the start',
            'Email field is now optional',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.2.0',
      release: 'Jul 15, 2020',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Manage user groups',
            'Add or edit project name for leads under sales information',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.1.0',
      release: 'Jul 09, 2020',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Assign leads to targeted sales teams via customisable user groups',
            '5 new intelligent rule options for lead auto assignment!',
            'New smart reassignment for inactive leads with 5 time-based rules to choose from',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '1.0.0',
      release: 'Jul 02, 2020',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Contact field settings is now available! Adjust the requirement, visibility and custom fields to suit your preference',
            'Navigate easily when editing a lead or contact using the bookmarks menu by the side!',
            'Download all lead or customer records',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '0.18.0',
      release: 'June 24, 2020',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Submissions from MHub forms will now always create a lead while customers are no longer linked to submissions.',
            'View the link to the submission from lead profile under submission source',
            'View the link to lead from submission under "View lead" when you click on the submission',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Table selection has been improved and select all records or by the page',
            'Expand table view to show more records',
          ],
        },
      ],
    },
    {
      version: '0.17.0',
      release: 'June 17, 2020',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Add unit number when qualifying lead',
            'Import leads now accepts assignee email, project id and campaign id',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Component upgrades',
            'View note previews at the new notes card on lead profile',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '0.16.0',
      release: 'June 10, 2020',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Customer profile has undergone a facelift and categorised according to stages for easier viewing',
            'Check out our new notes, appointment card and submissions in lead and customer profile page',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Campaigns are now being shared according to business unit regardless of your user role',
            'Staff user roles will have certain campaign restrictions and limited permissions',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '0.15.0',
      release: 'June 03, 2020',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Leads and customers are now connected to campaigns via form submissions',
            'A lead will be created from any unique submission',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '0.14.0',
      release: 'May 28, 2020',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Filter leads according to custom fields',
            'Sort and filter submissions',
            'View submissions according to forms by clicking on the arrow at the form',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '0.13.0',
      release: 'May 20, 2020',
      updates: [
        {
          category: 'new_features',
          notes: [
            'View list of submissions from MHub forms accesible via campaigns',
            'Make an appointment with a customer via opportunities',
            'Search for customers and contacts easily at the search bar',
          ],
        },
      ],
    },
    {
      version: '0.12.0',
      release: 'May 13, 2020',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Source field is now upgraded with a fixed set of values for easier tracking',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '0.11.0',
      release: 'May 06, 2020',
      updates: [
        {
          category: 'new_features',
          notes: [
            'New channel! Add details about your events to keep track',
          ],
        },
      ],
    },
    {
      version: '0.10.1',
      release: 'May 05, 2020',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '0.10.0',
      release: 'Apr 30, 2020',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Create campaigns to organise your brand and product messaging',
            'Create customisable forms to capture leads',
            'Share your sales kit via opportunity at the customer profile page',
          ],
        },
      ],
    },
    {
      version: '0.9.0',
      release: 'Apr 22, 2020',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Search for leads easily with the brand new search bar located at the top',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Upgraded component for better performance',
          ],
        },
      ],
    },
    {
      version: '0.8.1',
      release: 'Apr 17, 2020',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Fixes bugs that causes certain pages not to load properly',
          ],
        },
      ],
    },
    {
      version: '0.8.0',
      release: 'Apr 15, 2020',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Call, email and launch personal whatsapp easily from the new speed dial button',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Importing leads will now remove symbols and unwanted spaces for names',
          ],
        },
      ],
    },
    {
      version: '0.7.0',
      release: 'Apr 07, 2020',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Shift to select multiple items in the table',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Edit notes becomes easier with slight improvements',
            'Importing leads has been improved',
            'Sorting has been improved',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '0.6.0',
      release: 'Mar 31, 2020',
      updates: [
        {
          category: 'new_features',
          notes: [
            'For a better flow, qualifying leads now requires a project name to convert to customer',
            'Customer activity log to monitor sales activities',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Page loading has been improved',
            'You can now view the project name at the customer list',
            'Display for customers linked in opportunity listing has been improved',
            'UI improvements',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '0.5.0',
      release: 'Mar 24, 2020',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Automated email notifications to assignees',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Sort opportunities easily',
            'Access the help menu on mobile',
            'Custom fields from lead module will be synced with contact module',
            'UI improvements',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '0.4.0',
      release: 'Mar 19, 2020',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Make a reservation from every opportunity easily by using our booking app',
            'Tag opportunities as lost with lost reason to track feedback from customers',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'UI improvements',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '0.3.0',
      release: 'Mar 17, 2020',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Sharing settings between user roles has been improved',
            'UI improvements',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '0.2.1',
      release: 'Mar 13, 2020',
      updates: [
        {
          category: 'improvements',
          notes: [
            'Page loading has been improved',
            'Customer, contacts and opportunity sharing has been refined',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '0.2.0',
      release: 'Mar 10, 2020',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Share leads, contacts, customers and opportunities automatically within business units',
            'Auto-assign imported leads that have no assignee',
          ],
        },
        {
          category: 'improvements',
          notes: [
            'Importing leads sample file has been improved',
            'Mobile view has been improved',
            'Bug fixes',
          ],
        },
      ],
    },
    {
      version: '0.1.2',
      release: 'Mar 2, 2020',
      updates: [
        {
          category: 'new_features',
          notes: [
            'Maintain and monitor leads, contacts, customers and opportunities with tables using lists, filters and customised views',
            'Lead field settings with custom lead fields to support additional lead fields',
            'Lead and contact duplication checks and duplication prevention',
            'Sales pipeline monitoring for leads and opportunities',
            'Lead notes, activities and customer notes to monitor interaction',
            'Assign users to leads or opportunities',
          ],
        },
      ],
    },
  ];

  return (
    <div className={classes.container}>
      <Grid container className={classes.header}>
        <Grid container item justifyContent="space-between">
          <Grid item>
            <Typography variant="h1">MHub CRM</Typography>
          </Grid>
          <Grid item>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
        <Grid>
          <Typography>
            Discover new features with the brand-new CRM
          </Typography>
        </Grid>
      </Grid>

      {
        contents.map((content) => (
          <Grid container direction="column" key={content.version} className={classes.section}>
            <Grid
              item
              container
              className={classes.versionContainer}
              alignItems="baseline"
            >
              <Typography variant="h5">
                {`Version ${content.version}`}
              </Typography>
              <Typography variant="body2" className={classes.releaseText}>
                {`Released on ${content.release}`}
              </Typography>
            </Grid>

            {
              React.Children.toArray(content.updates.map((list) => (
                <>
                  <Grid item>
                    {renderTag(list.category)}
                  </Grid>
                  <Grid item>
                    <ul className={classes.list}>
                      {list.notes.map((noteItem) => {
                        const note = typeof noteItem === 'string'
                          ? { text: noteItem }
                          : noteItem;

                        // Hide release note if country_code is not in the note's onlyShowCountry
                        if (
                          Array.isArray(note.onlyShowCountry)
                          && !note.onlyShowCountry.includes(country.country_code)
                        ) {
                          return null;
                        }
                        return (
                          <li key={note.text}>
                            <span className={note.highlight ? classes.highlight : undefined}>
                              {note.onlyShowCountry && note.onlyShowCountry.includes('ID')
                                // add the localize key in here in case want to translate
                                ? t(note.text)
                                : note.text}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </Grid>
                </>
              )))
            }
          </Grid>
        ))
      }
    </div>
  );
};

NewsModal.propTypes = {
  onClose: PropTypes.func,
};

NewsModal.defaultProps = {
  onClose: () => {},
};

export default NewsModal;
